import { useState, useEffect, useCallback } from "react";
import { Navigate } from "react-router-dom";
import {
  Box, Typography, Button, ButtonGroup, TextField, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem, Tooltip,
  IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import EditNoteIcon from "@mui/icons-material/EditNote";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as ReTooltip,
  Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { useTranslation } from "react-i18next";
import Header from "../components/Header";
import { useThemeMode } from "../context/ThemeContext";
import { useAuth } from "../context/AuthContext";
import { applyDeltas } from "../utils/gaugeCalculations";

const SIT_DELTA_KEYS = ["us_logique","us_social","us_sensoriel","us_passion","us_motivation","us_reserve","us_stamina"];
const SIT_TO_GAUGE_MAP = [
  { sitKey: "us_logique",     gaugeKey: "currentLogique" },
  { sitKey: "us_social",     gaugeKey: "currentSocial" },
  { sitKey: "us_sensoriel",   gaugeKey: "currentSensoriel" },
  { sitKey: "us_passion",     gaugeKey: "currentPassion" },
  { sitKey: "us_motivation", gaugeKey: "currentMotivation" },
  { sitKey: "us_reserve",   gaugeKey: "currentReserve" },
  { sitKey: "us_stamina",     gaugeKey: "currentStamina" },
];
const DH_TO_CURRENT = {
  dh_logique:     "currentLogique",
  dh_social:     "currentSocial",
  dh_sensoriel:   "currentSensoriel",
  dh_passion:     "currentPassion",
  dh_motivation: "currentMotivation",
  dh_reserve:   "currentReserve",
  dh_stamina:     "currentStamina",
};
const getSitPolarity = (sit) => {
  const hasPos = SIT_DELTA_KEYS.some((k) => sit[k] > 0);
  const hasNeg = SIT_DELTA_KEYS.some((k) => sit[k] < 0);
  if (hasPos && !hasNeg) return "#86efac";
  if (hasNeg && !hasPos) return "#fca5a5";
  return "#93c5fd";
};

// ─── Couleurs des jauges (cohérentes avec EnergyGauges) ────────────────────
const GAUGE_CONFIG = [
  { key: "dh_logique",     labelKey: "history.gaugeLabels.logique",     color: "#14b8a6", max: 100 },
  { key: "dh_social",     labelKey: "history.gaugeLabels.social",     color: "#29b6f6", max: 100 },
  { key: "dh_sensoriel",   labelKey: "history.gaugeLabels.sensoriel",   color: "#ffa726", max: 100 },
  { key: "dh_passion",     labelKey: "history.gaugeLabels.passion",     color: "#fb7185", max: 100 },
  { key: "dh_motivation", labelKey: "history.gaugeLabels.motivation", color: "#a855f7", max: 100 },
  { key: "dh_reserve",   labelKey: "history.gaugeLabels.reserve",   color: "#f44336", max: 300 },
  { key: "dh_stamina",     labelKey: "history.gaugeLabels.stamina", color: "#42d750", max: 100 },
];

// ─── Fenêtres temporelles glissantes ───────────────────────────────────────
const WINDOWS = [
  { labelKey: "history.windows.week",    days: 7 },
  { labelKey: "history.windows.month",   days: 30 },
  { labelKey: "history.windows.quarter", days: 91 },
  { labelKey: "history.windows.year",    days: 365 },
];

const today = () => new Date().toISOString().slice(0, 10);
const daysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};

// Formate YYYY-MM-DD → "jj/mm" ou "jj/mm/aaaa"
const fmtDate = (str, withYear = false) => {
  if (!str) return "";
  const [y, m, d] = str.split("-");
  return withYear ? `${d}/${m}/${y}` : `${d}/${m}`;
};

// Normalise une valeur jauge sur 0–100 pour l'affichage (reserve /3)
const normalize = (key, value) => {
  const cfg = GAUGE_CONFIG.find((g) => g.key === key);
  if (!cfg) return value;
  return Math.round((value / cfg.max) * 100);
};

// Génère les palettes pour les années superposées (même teinte, plus transparent)
const yearColor = (baseColor, idx) => {
  // idx 0 = année en cours (pleine), 1 = N-1, 2 = N-2...
  const opacity = Math.max(0.25, 1 - idx * 0.25);
  const hex = baseColor.replace("#", "");
  const r = parseInt(hex.slice(0, 2), 16);
  const g = parseInt(hex.slice(2, 4), 16);
  const b = parseInt(hex.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${opacity})`;
};

// ─── Marqueur personnalisé pour les jours squizés ──────────────────────────
const SkippedDot = (props) => {
  const { cx, cy, payload } = props;
  if (!payload?.skipped) return null;
  return (
    <g>
      <circle cx={cx} cy={cy} r={7} fill="#64748b" stroke="#94a3b8" strokeWidth={1} />
      <text x={cx} y={cy + 4} textAnchor="middle" fontSize={9} fill="#e2e8f0">✕</text>
    </g>
  );
};

// ─── Tooltip personnalisé ──────────────────────────────────────────────────
const CustomTooltip = ({ active, payload, label }) => {
  const { t } = useTranslation();
  if (!active || !payload?.length) return null;
  const isSkipped = payload[0]?.payload?.skipped;
  return (
    <Box sx={{ backgroundColor: "#0f172a", border: "1px solid #1e293b", borderRadius: 1, p: 1, minWidth: 180 }}>
      <Typography sx={{ color: "#94a3b8", fontSize: "0.75rem", mb: 0.5 }}>{label}</Typography>
      {isSkipped ? (
        <Typography sx={{ color: "#64748b", fontSize: "0.8rem", fontStyle: "italic" }}>
          {t("history.skippedDay", { reason: payload[0]?.payload?.skipReason === "rest" ? t("history.skipReasonRest") : t("history.skipReasonKO") })}
        </Typography>
      ) : (
        payload.map((p) => (
          <Box key={p.dataKey} sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.25 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: p.color, flexShrink: 0 }} />
            <Typography sx={{ color: "#e2e8f0", fontSize: "0.78rem" }}>
              {p.name}: <strong>{p.value}%</strong>
            </Typography>
          </Box>
        ))
      )}
    </Box>
  );
};

// ─── Composant principal ───────────────────────────────────────────────────
export default function History() {
  const { t } = useTranslation();
  const { state: user } = useAuth();
  const { appTheme } = useThemeMode();

  const [history, setHistory] = useState([]);
  const [currentRange, setCurrentRange] = useState({ from: daysAgo(30), to: today() });
  const [windowDays, setWindowDays] = useState(30);
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [seasonalMode, setSeasonalMode] = useState(false);
  const [visibleGauges, setVisibleGauges] = useState(
    Object.fromEntries(GAUGE_CONFIG.map((g) => [g.key, true]))
  );

  // Dialog skip
  const [skipDialogOpen, setSkipDialogOpen] = useState(false);
  const [skipDate, setSkipDate] = useState(today());
  const [skipReason, setSkipReason] = useState("ko");
  const [skipLoading, setSkipLoading] = useState(false);

  // Dialog saisie manuelle
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [manualDate, setManualDate] = useState("");
  const [manualMode, setManualMode] = useState("situations"); // "situations" | "values"
  const [manualValues, setManualValues] = useState(
    Object.fromEntries(GAUGE_CONFIG.map((g) => [g.key, 0]))
  );
  const [manualLoading, setManualLoading] = useState(false);
  const [manualSits, setManualSits] = useState([]);
  const [manualSelected, setManualSelected] = useState([]);
  const [manualBaseProfile, setManualBaseProfile] = useState(null);
  const [manualSearch, setManualSearch] = useState("");

  useEffect(() => {
    if (!manualDialogOpen) return;
    if (manualSits.length === 0) {
      fetch("/api/situations", { credentials: "include" })
        .then((r) => (r.ok ? r.json() : []))
        .then((data) => setManualSits(data.filter((s) => s.us_active)))
        .catch(() => {});
    }
    if (!manualBaseProfile) {
      fetch("/api/gauges/defaults", { credentials: "include" })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data) return;
          setManualBaseProfile({
            Logique:     data.ug_defaultLogique     ?? 100,
            Social:     data.ug_defaultSocial     ?? 100,
            Sensoriel:   data.ug_defaultSensoriel   ?? 100,
            Passion:     data.ug_defaultPassion     ?? 100,
            Motivation: data.ug_defaultMotivation ?? 100,
            Reserve:   data.ug_defaultReserve   ?? 300,
            Stamina:     data.ug_defaultStamina     ?? 100,
          });
        })
        .catch(() => {});
    }
  }, [manualDialogOpen]); // eslint-disable-line

  const fetchHistory = useCallback((from, to) => {
    setCurrentRange({ from, to });
    fetch(`/api/history?from=${from}&to=${to}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : []))
      .then(setHistory)
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (useCustomRange && customFrom && customTo) {
      fetchHistory(customFrom, customTo);
    } else {
      fetchHistory(daysAgo(windowDays), today());
    }
  }, [windowDays, useCustomRange, customFrom, customTo, fetchHistory]);

  if (!user) return <Navigate to="/connexion" replace />;

  // ─── Prépare les données du graphique ──────────────────────────────────

  // Mode normal : génère TOUTES les dates de la plage, null pour les jours sans données
  const buildChartData = () => {
    const byDate = Object.fromEntries(history.map((r) => [r.dh_date, r]));

    const points = [];
    const start = new Date(currentRange.from);
    const end = new Date(currentRange.to);
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const iso = d.toISOString().slice(0, 10);
      const row = byDate[iso];
      const point = {
        date: fmtDate(iso),
        rawDate: iso,
        skipped: row?.dh_skipped ?? false,
        skipReason: row?.dh_skip_reason ?? null,
      };
      if (row && !row.dh_skipped) {
        for (const { key } of GAUGE_CONFIG) {
          point[key] = normalize(key, row[key]);
        }
      } else {
        for (const { key } of GAUGE_CONFIG) {
          point[key] = null;
        }
      }
      points.push(point);
    }
    return points;
  };

  // Mode saisonnier : superpose les années sur le même axe mois/jour
  const buildSeasonalData = () => {
    // Grouper par année
    const byYear = {};
    for (const row of history) {
      const [y, m, d] = row.dh_date.split("-");
      const year = y;
      const dayOfYear = `${m}-${d}`;
      if (!byYear[year]) byYear[year] = {};
      byYear[year][dayOfYear] = row;
    }

    const years = Object.keys(byYear).sort().reverse(); // plus récent en premier

    // Collecter tous les jours uniques (mm-dd)
    const allDays = [...new Set(
      history.map((r) => r.dh_date.slice(5)) // mm-dd
    )].sort();

    return { years, allDays, byYear };
  };

  const chartData = buildChartData();

  // ─── Gestion des jours squizés ─────────────────────────────────────────

  const handleSkipSubmit = async () => {
    setSkipLoading(true);
    try {
      await fetch("/api/history/skip", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ date: skipDate, reason: skipReason }),
      });
      setSkipDialogOpen(false);
      // Rafraîchir
      if (useCustomRange && customFrom && customTo) {
        fetchHistory(customFrom, customTo);
      } else {
        fetchHistory(daysAgo(windowDays), today());
      }
    } finally {
      setSkipLoading(false);
    }
  };

  const computeSituationsPreview = () => {
    if (!manualBaseProfile) return null;
    const maxGauges = { Logique: 100, Social: 100, Sensoriel: 100, Passion: 100, Motivation: 100, Reserve: 300, Stamina: 100 };
    let state = {
      currentLogique:     manualBaseProfile.Logique,
      currentSocial:     manualBaseProfile.Social,
      currentSensoriel:   manualBaseProfile.Sensoriel,
      currentPassion:     manualBaseProfile.Passion,
      currentMotivation: manualBaseProfile.Motivation,
      currentReserve:   manualBaseProfile.Reserve,
      currentStamina:     manualBaseProfile.Stamina,
    };
    for (const sit of manualSelected) {
      const deltas = {};
      for (const { sitKey, gaugeKey } of SIT_TO_GAUGE_MAP) {
        const delta = sit[sitKey] ?? 0;
        if (delta !== 0) deltas[gaugeKey] = delta;
      }
      if (Object.keys(deltas).length > 0) {
        state = { ...state, ...applyDeltas(deltas, state, manualBaseProfile, maxGauges) };
      }
    }
    return state;
  };

  const handleManualSubmit = async () => {
    setManualLoading(true);
    try {
      let body;
      if (manualMode === "situations") {
        const preview = computeSituationsPreview();
        if (!preview) { setManualLoading(false); return; }
        body = {
          date: manualDate,
          dh_logique:     preview.currentLogique,
          dh_social:     preview.currentSocial,
          dh_sensoriel:   preview.currentSensoriel,
          dh_passion:     preview.currentPassion,
          dh_motivation: preview.currentMotivation,
          dh_reserve:   preview.currentReserve,
          dh_stamina:     preview.currentStamina,
        };
      } else {
        body = { date: manualDate, ...manualValues };
      }
      await fetch("/api/history/manual", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setManualDialogOpen(false);
      if (useCustomRange && customFrom && customTo) {
        fetchHistory(customFrom, customTo);
      } else {
        fetchHistory(daysAgo(windowDays), today());
      }
    } finally {
      setManualLoading(false);
    }
  };

  const handleUnskip = async (date) => {
    await fetch(`/api/history/skip/${date}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (useCustomRange && customFrom && customTo) {
      fetchHistory(customFrom, customTo);
    } else {
      fetchHistory(daysAgo(windowDays), today());
    }
  };

  // ─── Toggle visibilité d'une jauge ─────────────────────────────────────
  const toggleGauge = (key) => {
    setVisibleGauges((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // ─── Rendu saisonnier ──────────────────────────────────────────────────
  const renderSeasonal = () => {
    const { years, allDays, byYear } = buildSeasonalData();
    if (years.length < 1) return (
      <Typography sx={{ color: "#64748b", textAlign: "center", mt: 4 }}>
        {t("history.notEnoughSeasonal")}
      </Typography>
    );

    const data = allDays.map((mmdd) => {
      const point = { mmdd: mmdd.replace("-", "/") };
      for (const year of years) {
        const row = byYear[year]?.[mmdd];
        if (row && !row.dh_skipped) {
          for (const { key } of GAUGE_CONFIG) {
            point[`${key}_${year}`] = normalize(key, row[key]);
          }
        }
      }
      return point;
    });

    return (
      <ResponsiveContainer width="100%" height={420}>
        <LineChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
          <XAxis
            dataKey="mmdd"
            tick={{ fill: "#64748b", fontSize: 11 }}
            angle={-45}
            textAnchor="end"
            interval={Math.max(0, Math.floor(allDays.length / 20))}
          />
          <YAxis domain={[0, 100]} tick={{ fill: "#64748b", fontSize: 11 }} unit="%" />
          <ReTooltip
            contentStyle={{ backgroundColor: "#0f172a", border: "1px solid #1e293b", borderRadius: 4 }}
            labelStyle={{ color: "#94a3b8", fontSize: 12 }}
            itemStyle={{ fontSize: 12 }}
          />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 12 }} />
          {GAUGE_CONFIG.filter((g) => visibleGauges[g.key]).map((g) =>
            years.map((year, idx) => (
              <Line
                key={`${g.key}_${year}`}
                type="monotone"
                dataKey={`${g.key}_${year}`}
                name={`${t(g.labelKey)} ${year}`}
                stroke={yearColor(g.color, idx)}
                strokeWidth={idx === 0 ? 2 : 1.5}
                strokeDasharray={idx > 0 ? "4 3" : undefined}
                dot={false}
                connectNulls={false}
              />
            ))
          )}
        </LineChart>
      </ResponsiveContainer>
    );
  };

  // ─── Rendu principal ───────────────────────────────────────────────────
  const skippedDays = history.filter((r) => r.dh_skipped);

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: appTheme.bg, color: appTheme.text }}>
      <Header />

      <Box sx={{ maxWidth: 1100, mx: "auto", px: { xs: 2, md: 3 }, py: 3 }}>

        {/* Titre */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2, flexWrap: "wrap", gap: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: appTheme.text }}>
            {t("history.title")}
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EditNoteIcon />}
              onClick={() => {
                setManualDate(daysAgo(1));
                setManualValues(Object.fromEntries(GAUGE_CONFIG.map((g) => [g.key, 0])));
                setManualDialogOpen(true);
              }}
              sx={{ borderColor: "#334155", color: "#94a3b8", "&:hover": { borderColor: "#64748b" } }}
            >
              {t("history.manualEntry")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EventBusyIcon />}
              onClick={() => { setSkipDate(today()); setSkipDialogOpen(true); }}
              sx={{ borderColor: "#334155", color: "#94a3b8", "&:hover": { borderColor: "#64748b" } }}
            >
              {t("history.markSkipped")}
            </Button>
          </Box>
        </Box>

        {/* Sélection de la plage temporelle */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
          <ButtonGroup size="small" variant="outlined">
            {WINDOWS.map((w) => (
              <Button
                key={w.days}
                onClick={() => { setWindowDays(w.days); setUseCustomRange(false); }}
                variant={!useCustomRange && windowDays === w.days ? "contained" : "outlined"}
                sx={{
                  borderColor: "#334155", color: "#94a3b8",
                  "&.MuiButton-contained": { backgroundColor: "#1e3a5f", color: "#93c5fd", borderColor: "#1e3a5f" },
                }}
              >
                {t(w.labelKey)}
              </Button>
            ))}
          </ButtonGroup>

          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField
              type="date"
              size="small"
              label={t("history.from")}
              value={customFrom}
              onChange={(e) => { setCustomFrom(e.target.value); setUseCustomRange(true); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today() }}
              sx={dateFieldSx}
            />
            <TextField
              type="date"
              size="small"
              label={t("history.to")}
              value={customTo}
              onChange={(e) => { setCustomTo(e.target.value); setUseCustomRange(true); }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: today() }}
              sx={dateFieldSx}
            />
          </Box>

          {/* Tendances saisonnières */}
          <Tooltip title={t("history.overlayYears")}>
            <Button
              size="small"
              variant={seasonalMode ? "contained" : "outlined"}
              onClick={() => setSeasonalMode((v) => !v)}
              sx={{
                borderColor: "#334155", color: "#94a3b8", ml: "auto",
                "&.MuiButton-contained": { backgroundColor: "#4a1d96", color: "#c4b5fd", borderColor: "#4a1d96" },
              }}
            >
              {t("history.seasonal")}
            </Button>
          </Tooltip>
        </Box>

        {/* Filtres de jauges */}
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", alignItems: "center", mb: 2 }}>
          {GAUGE_CONFIG.map((g) => (
            <Chip
              key={g.key}
              label={t(g.labelKey)}
              size="small"
              onClick={() => toggleGauge(g.key)}
              sx={{
                backgroundColor: visibleGauges[g.key] ? g.color + "33" : "#1e293b",
                color: visibleGauges[g.key] ? g.color : "#475569",
                border: `1px solid ${visibleGauges[g.key] ? g.color + "88" : "#334155"}`,
                cursor: "pointer",
                fontSize: "0.72rem",
                "&:hover": { backgroundColor: g.color + "44" },
              }}
            />
          ))}
          <Tooltip title={t("history.deselectAll")}>
            <IconButton
              size="small"
              onClick={() => {
                const allHidden = GAUGE_CONFIG.every((g) => !visibleGauges[g.key]);
                setVisibleGauges(Object.fromEntries(GAUGE_CONFIG.map((g) => [g.key, allHidden])));
              }}
              sx={{ color: "#475569", "&:hover": { color: "#94a3b8" }, ml: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Note sur reserve */}
        <Typography sx={{ color: "#475569", fontSize: "0.7rem", mb: 1.5, fontStyle: "italic" }}>
          {t("history.normalizedNote")}
        </Typography>

        {/* Graphique */}
        <Box sx={{ backgroundColor: "#0a1628", borderRadius: 2, p: 2, mb: 2 }}>
          {history.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 6 }}>
              <Typography sx={{ color: "#475569", fontSize: "0.9rem" }}>
                {t("history.noData")}
              </Typography>
              <Typography sx={{ color: "#334155", fontSize: "0.8rem", mt: 1 }}>
                {t("history.autoFill")}
              </Typography>
            </Box>
          ) : seasonalMode ? (
            renderSeasonal()
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <LineChart data={chartData} margin={{ top: 10, right: 20, left: 0, bottom: 40 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" />
                <XAxis
                  dataKey="date"
                  tick={{ fill: "#64748b", fontSize: 11 }}
                  angle={-45}
                  textAnchor="end"
                  interval={Math.max(0, Math.floor(chartData.length / 20))}
                />
                <YAxis domain={[-50, 100]} tick={{ fill: "#64748b", fontSize: 11 }} unit="%" />
                <ReTooltip content={<CustomTooltip />} />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 12 }}
                  formatter={(value) => <span style={{ color: "#94a3b8" }}>{value}</span>}
                />
                <ReferenceLine y={0} stroke="#334155" strokeDasharray="4 2" />

                {GAUGE_CONFIG.filter((g) => visibleGauges[g.key]).map((g) => (
                  <Line
                    key={g.key}
                    type="monotone"
                    dataKey={g.key}
                    name={t(g.labelKey)}
                    stroke={g.color}
                    strokeWidth={2}
                    dot={(props) => <SkippedDot {...props} />}
                    activeDot={{ r: 4, strokeWidth: 0 }}
                    connectNulls={false}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          )}
        </Box>

        {/* Jours squizés listés */}
        {skippedDays.length > 0 && (
          <Box sx={{ backgroundColor: "#0a1628", borderRadius: 2, p: 2 }}>
            <Typography sx={{ color: "#64748b", fontSize: "0.8rem", mb: 1, fontWeight: 600 }}>
              {t("history.skippedList")}
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
              {skippedDays.map((row) => (
                <Chip
                  key={row.dh_date}
                  label={`${fmtDate(row.dh_date, true)} — ${row.dh_skip_reason === "rest" ? t("history.skipReasonRest") : t("history.skipReasonKO")}`}
                  size="small"
                  onDelete={() => handleUnskip(row.dh_date)}
                  sx={{
                    backgroundColor: "#1e293b",
                    color: "#94a3b8",
                    border: "1px solid #334155",
                    fontSize: "0.75rem",
                    "& .MuiChip-deleteIcon": { color: "#64748b", "&:hover": { color: "#f87171" } },
                  }}
                />
              ))}
            </Box>
          </Box>
        )}

      </Box>

      {/* ─── Dialog : saisie manuelle ────────────────────────────────────── */}
      <Dialog
        open={manualDialogOpen}
        onClose={() => setManualDialogOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#0f172a", color: "#e2e8f0", width: 580, maxWidth: "95vw", maxHeight: "88vh" } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontWeight: 700 }}>{t("history.manualEntryTitle")}</Typography>
          <IconButton onClick={() => setManualDialogOpen(false)} size="small" sx={{ color: "#64748b" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2, overflowY: "auto" }}>

          {/* Sélecteur de date */}
          <TextField
            type="date"
            label={t("history.date")}
            value={manualDate}
            onChange={(e) => setManualDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today() }}
            size="small"
            fullWidth
            sx={dateFieldSx}
          />

          {/* Toggle mode */}
          <ButtonGroup size="small" fullWidth>
            {[
              { key: "situations", label: t("history.manualModeSituations") },
              { key: "values",     label: t("history.manualModeValues") },
            ].map(({ key, label }) => (
              <Button
                key={key}
                onClick={() => setManualMode(key)}
                variant={manualMode === key ? "contained" : "outlined"}
                sx={{
                  borderColor: "#334155", color: "#94a3b8", fontSize: "0.78rem",
                  "&.MuiButton-contained": { backgroundColor: "#1e3a5f", color: "#93c5fd", borderColor: "#1e3a5f" },
                }}
              >
                {label}
              </Button>
            ))}
          </ButtonGroup>

          {/* ── Mode situations ─────────────────────────────────────────── */}
          {manualMode === "situations" && (() => {
            const filteredSits = manualSits.filter((s) => {
              if (!manualSearch.trim()) return true;
              const q = manualSearch.toLowerCase();
              return (s.us_label_fr ?? "").toLowerCase().includes(q) || (s.us_label_en ?? "").toLowerCase().includes(q);
            });
            const preview = computeSituationsPreview();
            return (
              <>
                {/* Layout 2 colonnes : liste dispo | sélectionnées */}
                <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.5, alignItems: "stretch" }}>

                  {/* Colonne gauche : recherche + liste */}
                  <Box sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 1, minWidth: 0 }}>
                    <TextField
                      size="small"
                      placeholder={t("history.manualSearchSit")}
                      value={manualSearch}
                      onChange={(e) => setManualSearch(e.target.value)}
                      sx={numFieldSx}
                    />
                    <Box sx={{ flex: 1, minHeight: 140, maxHeight: { xs: 180, sm: 240 }, overflowY: "auto",
                      display: "flex", flexWrap: "wrap", alignContent: "flex-start", gap: 0.5, pr: 0.5,
                      maskImage: "linear-gradient(to bottom, black calc(100% - 20px), transparent 100%)" }}>
                      {filteredSits.map((sit) => {
                        const col = getSitPolarity(sit);
                        const count = manualSelected.filter((s) => s.us_ID === sit.us_ID).length;
                        const lang = document.documentElement.lang === "en" ? "en" : "fr";
                        return (
                          <Chip
                            key={sit.us_ID}
                            size="small"
                            label={count > 0 ? `${sit[`us_label_${lang}`] ?? sit.us_label_fr} ×${count}` : (sit[`us_label_${lang}`] ?? sit.us_label_fr)}
                            onClick={() => setManualSelected((prev) => [...prev, sit])}
                            sx={{ backgroundColor: count > 0 ? col + "22" : "#1e293b", color: col,
                              border: count > 0 ? `1px solid ${col}55` : `1px solid #334155`,
                              cursor: "pointer", fontSize: "0.72rem", "&:hover": { backgroundColor: col + "22", borderColor: col + "55" } }}
                          />
                        );
                      })}
                    </Box>
                  </Box>

                  {/* Colonne droite : sélectionnées */}
                  <Box sx={{ width: { xs: "100%", sm: 170 }, flexShrink: 0, display: "flex", flexDirection: "column", gap: 0.75 }}>
                    <Typography sx={{ color: "#475569", fontSize: "0.7rem" }}>
                      {t("history.manualModeSituations")} ({manualSelected.length})
                    </Typography>
                    <Box sx={{ flex: 1, minHeight: { xs: 64, sm: 140 }, maxHeight: { xs: 100, sm: 240 }, overflowY: "auto",
                      display: "flex", flexDirection: "column", gap: 0.5, pr: 0.25,
                      maskImage: "linear-gradient(to bottom, black calc(100% - 12px), transparent 100%)" }}>
                      {manualSelected.length === 0 ? (
                        <Typography sx={{ color: "#334155", fontSize: "0.68rem", fontStyle: "italic", pt: 0.5 }}>—</Typography>
                      ) : manualSelected.map((sit, idx) => {
                        const col = getSitPolarity(sit);
                        const lang = document.documentElement.lang === "en" ? "en" : "fr";
                        return (
                          <Chip
                            key={idx}
                            size="small"
                            label={sit[`us_label_${lang}`] ?? sit.us_label_fr}
                            onDelete={() => setManualSelected((prev) => prev.filter((_, i) => i !== idx))}
                            sx={{ backgroundColor: col + "22", color: col, border: `1px solid ${col}55`, fontSize: "0.72rem",
                              "& .MuiChip-deleteIcon": { color: col + "99", "&:hover": { color: col } } }}
                          />
                        );
                      })}
                    </Box>
                  </Box>

                </Box>

                {/* Preview */}
                {manualBaseProfile && (
                  <Box sx={{ backgroundColor: "#0a1628", borderRadius: 1, p: 1.5 }}>
                    <Typography sx={{ color: "#475569", fontSize: "0.72rem", mb: 1 }}>
                      {t("history.manualPreviewFrom")}
                    </Typography>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 12px" }}>
                      {GAUGE_CONFIG.map((g) => {
                        const raw = preview ? (preview[DH_TO_CURRENT[g.key]] ?? 0) : manualBaseProfile[g.key.replace("dh_m", "M").replace("dh_s","S").replace("ana","ana")] ?? 0;
                        const pct = Math.round(Math.max(0, (raw / g.max) * 100));
                        return (
                          <Box key={g.key} sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                            <Typography sx={{ color: "#64748b", fontSize: "0.68rem", width: 68, flexShrink: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                              {t(g.labelKey)}
                            </Typography>
                            <Box sx={{ flex: 1, height: 4, backgroundColor: "#1e293b", borderRadius: 1, minWidth: 30 }}>
                              <Box sx={{ width: `${pct}%`, height: "100%", backgroundColor: g.color, borderRadius: 1, transition: "width 0.2s" }} />
                            </Box>
                            <Typography sx={{ color: g.color, fontSize: "0.68rem", width: 28, textAlign: "right", flexShrink: 0 }}>
                              {pct}%
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  </Box>
                )}
              </>
            );
          })()}

          {/* ── Mode valeurs directes ────────────────────────────────────── */}
          {manualMode === "values" && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
              {GAUGE_CONFIG.map((g) => (
                <TextField
                  key={g.key}
                  type="number"
                  label={`${t(g.labelKey)} (0–${g.max})`}
                  value={manualValues[g.key]}
                  onChange={(e) => {
                    const val = Math.min(g.max, Math.max(0, Number(e.target.value)));
                    setManualValues((prev) => ({ ...prev, [g.key]: val }));
                  }}
                  inputProps={{ min: 0, max: g.max, step: 1 }}
                  size="small"
                  sx={numFieldSx}
                />
              ))}
            </Box>
          )}

        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setManualDialogOpen(false)} sx={{ color: "#64748b" }}>{t("common.cancelBtn")}</Button>
          <Button
            onClick={handleManualSubmit}
            disabled={manualLoading || !manualDate || (manualMode === "situations" && !manualBaseProfile)}
            variant="contained"
            sx={{ backgroundColor: "#1e3a5f", color: "#93c5fd", "&:hover": { backgroundColor: "#1e40af" } }}
          >
            {t("history.confirm")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ─── Dialog : marquer un jour squizé ─────────────────────────────── */}
      <Dialog
        open={skipDialogOpen}
        onClose={() => setSkipDialogOpen(false)}
        PaperProps={{ sx: { backgroundColor: "#0f172a", color: "#e2e8f0", minWidth: 320 } }}
      >
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontWeight: 700 }}>{t("history.markSkipped")}</Typography>
          <IconButton onClick={() => setSkipDialogOpen(false)} size="small" sx={{ color: "#64748b" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, display: "flex", flexDirection: "column", gap: 2 }}>
          <Typography sx={{ color: "#64748b", fontSize: "0.85rem" }}>
            {t("history.skipDialogDesc")}
          </Typography>
          <TextField
            type="date"
            label={t("history.date")}
            value={skipDate}
            onChange={(e) => setSkipDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: today() }}
            size="small"
            fullWidth
            sx={dateFieldSx}
          />
          <FormControl size="small" fullWidth>
            <InputLabel sx={{ color: "#64748b" }}>{t("history.reason")}</InputLabel>
            <Select
              value={skipReason}
              onChange={(e) => setSkipReason(e.target.value)}
              label={t("history.reason")}
              sx={{ color: "#e2e8f0", "& .MuiOutlinedInput-notchedOutline": { borderColor: "#334155" } }}
            >
              <MenuItem value="ko">{t("history.reasonKO")}</MenuItem>
              <MenuItem value="rest">{t("history.reasonRest")}</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ backgroundColor: "#1e293b", borderRadius: 1, p: 1.5 }}>
            <Typography sx={{ color: "#94a3b8", fontSize: "0.78rem", lineHeight: 1.5 }}>
              {skipReason === "ko" ? t("history.skipDayKO") : t("history.skipDayRest")}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, pb: 2 }}>
          <Button onClick={() => setSkipDialogOpen(false)} sx={{ color: "#64748b" }}>{t("common.cancelBtn")}</Button>
          <Button
            onClick={handleSkipSubmit}
            disabled={skipLoading || !skipDate}
            variant="contained"
            sx={{ backgroundColor: "#1e3a5f", color: "#93c5fd", "&:hover": { backgroundColor: "#1e40af" } }}
          >
            {t("history.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

// ─── Styles partagés ────────────────────────────────────────────────────────
const numFieldSx = {
  "& .MuiInputBase-input": { color: "#e2e8f0", fontSize: "0.85rem" },
  "& .MuiInputLabel-root": { color: "#64748b", fontSize: "0.8rem" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#334155" },
};

const dateFieldSx = {
  "& .MuiInputBase-input": { color: "#e2e8f0", fontSize: "0.85rem" },
  "& .MuiInputLabel-root": { color: "#64748b" },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: "#334155" },
  "& .MuiSvgIcon-root": { color: "#64748b" },
  "& input::-webkit-calendar-picker-indicator": { filter: "invert(0.5)" },
};
